import { formatCurrency, getActiveCurrency } from './currency';
import { isUaeProperty, calculateDubaiHospitalityTax, calculateDubaiFolioBreakdown, extractUaeCustomRates } from './dubaiHospitalityTax';

export const exportClientSide = (format, dataArray, filenameBase) => {
    if (!dataArray || dataArray.length === 0) {
        throw new Error('No data available to export');
    }

    const flattenObject = (obj, prefix = '') => {
        return Object.keys(obj).reduce((acc, k) => {
            // Filter out common internal database fields to keep exports clean
            const lowerK = k.toLowerCase();
            if (['id', '_id', 'tenantid', 'createdat', 'updatedat', '__v', 'password', 'passwordhash', 'v', 'overridereason', 'overridenote', 'reason', 'remarks', 'reasontext'].includes(lowerK) || 
                (lowerK.endsWith('id') && lowerK !== 'employeeid')) {
                return acc;
            }
            
            const pre = prefix.length ? prefix + '_' : '';
            if (typeof obj[k] === 'object' && obj[k] !== null && !Array.isArray(obj[k])) {
                Object.assign(acc, flattenObject(obj[k], pre + k));
            } else if (!Array.isArray(obj[k])) {
                acc[pre + k] = obj[k] !== undefined && obj[k] !== null ? obj[k] : '';
            }
            return acc;
        }, {});
    };

    const flatData = dataArray.map(item => flattenObject(item));
    const headers = Array.from(new Set(flatData.flatMap(Object.keys)));

    let blob;
    let extension;
    let mimeType;

    if (format === 'csv') {
        const csvContent = [
            headers.join(','),
            ...flatData.map(row => headers.map(fieldName => `"${String(row[fieldName]).replace(/"/g, '""')}"`).join(','))
        ].join('\n');
        blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        extension = 'csv';
    } else if (format === 'excel') {
        // Create an HTML table which Excel can open
        const html = `
            <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
            <head><meta charset="utf-8"></head>
            <body>
                <table>
                    <thead>
                        <tr>${headers.map(h => `<th>${String(h).toUpperCase()}</th>`).join('')}</tr>
                    </thead>
                    <tbody>
                        ${flatData.map(row => `<tr>${headers.map(h => `<td>${row[h]}</td>`).join('')}</tr>`).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;
        blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
        extension = 'xls';
    } else if (format === 'pdf') {
        const loadScript = (src) => {
            return new Promise((resolve, reject) => {
                if (document.querySelector(`script[src="${src}"]`)) {
                    resolve();
                    return;
                }
                const script = document.createElement('script');
                script.src = src;
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
        };

        // Load jsPDF dynamically to create an actual PDF file instead of using Print dialog
        Promise.all([
            loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js')
        ]).then(() => {
            return loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.31/jspdf.plugin.autotable.min.js');
        }).then(() => {
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('landscape'); // Use landscape to fit more columns
            
            const title = filenameBase.replace(/_/g, ' ').toUpperCase();
            doc.setFontSize(16);
            doc.text(title, 14, 15);
            
            const tableData = flatData.map(row => headers.map(h => String(row[h] || '')));
            
            doc.autoTable({
                head: [headers.map(h => String(h).toUpperCase())],
                body: tableData,
                startY: 20,
                styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
                headStyles: { fillColor: [25, 118, 210] }, // Match primary color
                horizontalPageBreak: true,
                horizontalPageBreakRepeat: 0,
            });
            
            doc.save(`${filenameBase}.pdf`);
        }).catch(err => {
            console.error("Failed to load PDF libraries:", err);
            alert("Failed to load PDF generation libraries. Please ensure you have an active internet connection.");
        });
        
        return;
    }

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', `${filenameBase}.${extension}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const convertCsvToExportFormat = async (csvText, format, filenameBase) => {
    if (format === 'csv') {
        const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filenameBase}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        return;
    }

    const rows = csvText.split('\n')
        .map(row => row.split(',').map(cell => cell.replace(/"/g, '').trim()))
        .filter(row => row.length > 0 && row.some(cell => cell !== ''));

    if (format === 'excel') {
        const loadScript = (src) => {
            return new Promise((resolve, reject) => {
                if (document.querySelector(`script[src="${src}"]`)) {
                    resolve(); return;
                }
                const script = document.createElement('script');
                script.src = src; script.onload = resolve; script.onerror = reject;
                document.head.appendChild(script);
            });
        };
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js');
        const XLSX = window.XLSX;
        const ws = XLSX.utils.aoa_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Export");
        XLSX.writeFile(wb, `${filenameBase}.xlsx`);
    } else if (format === 'pdf') {
        const loadScript = (src) => {
            return new Promise((resolve, reject) => {
                if (document.querySelector(`script[src="${src}"]`)) {
                    resolve(); return;
                }
                const script = document.createElement('script');
                script.src = src; script.onload = resolve; script.onerror = reject;
                document.head.appendChild(script);
            });
        };

        try {
            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.31/jspdf.plugin.autotable.min.js');
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('landscape');
            
            const headers = rows[0] || [];
            const body = rows.slice(1) || [];
            
            const title = filenameBase.replace(/_/g, ' ').toUpperCase();
            doc.setFontSize(16);
            doc.text(title, 14, 15);
            
            doc.autoTable({
                head: [headers],
                body: body,
                startY: 20,
                styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
                headStyles: { fillColor: [25, 118, 210] },
                horizontalPageBreak: true,
                horizontalPageBreakRepeat: 0,
            });
            
            doc.save(`${filenameBase}.pdf`);
        } catch (err) {
            console.error("Failed to load PDF libraries:", err);
            alert("Failed to load PDF generation libraries. Please ensure you have an active internet connection.");
        }
    }
};

export const generateReceiptPdf = async (folio, txn, propertyInfo = null) => {
    const loadScript = (src) => {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve(); return;
            }
            const script = document.createElement('script');
            script.src = src; script.onload = resolve; script.onerror = reject;
            document.head.appendChild(script);
        });
    };

    try {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.31/jspdf.plugin.autotable.min.js');
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF('portrait');

        const activeCur = getActiveCurrency();
        let adminAuth = {};
        try {
            adminAuth = JSON.parse(localStorage.getItem('adminAuth') || '{}');
        } catch (_) {}

        const isUae = (propertyInfo?.currency === 'AED') ||
                      (folio?.currency === 'AED') ||
                      (activeCur === 'AED') ||
                      (localStorage.getItem('activeCurrency') === 'AED') ||
                      isUaeProperty(propertyInfo) ||
                      isUaeProperty(adminAuth?.tenant) ||
                      isUaeProperty(adminAuth?.hotel) ||
                      adminAuth?.currency === 'AED';

        const rawCurrency = propertyInfo?.currency || folio?.currency || activeCur;
        const currency = isUae ? ((rawCurrency && rawCurrency !== 'INR') ? rawCurrency : 'AED') : (rawCurrency || 'INR');
        const propertyName = propertyInfo?.name || propertyInfo?.hotelName || adminAuth?.hotel?.name || adminAuth?.tenant?.name || "Willmount Resort";
        const propertyAddress = [propertyInfo?.address, propertyInfo?.city, propertyInfo?.country].filter(Boolean).join(', ') ||
                                [adminAuth?.hotel?.address, adminAuth?.hotel?.city, adminAuth?.hotel?.country].filter(Boolean).join(', ');
        const taxLabel = isUae ? 'TRN' : 'GSTIN';
        const taxNumber = propertyInfo?.trn || propertyInfo?.trnNumber || propertyInfo?.gstin || propertyInfo?.taxId || adminAuth?.hotel?.trn || adminAuth?.tenant?.trn || '';

        // Hotel Header
        doc.setFontSize(22);
        doc.setTextColor(33, 147, 176); 
        doc.text(propertyName, 105, 20, { align: "center" });
        
        let headerY = 26;
        if (propertyAddress) {
            doc.setFontSize(9);
            doc.setTextColor(120, 120, 120);
            doc.text(propertyAddress, 105, headerY, { align: "center" });
            headerY += 5;
        }

        if (taxNumber) {
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text(`${taxLabel}: ${taxNumber}`, 105, headerY, { align: "center" });
            headerY += 5;
        }

        doc.setFontSize(12);
        doc.setTextColor(70, 70, 70);
        doc.text("Official Transaction Receipt", 105, headerY + 2, { align: "center" });

        // Separator
        doc.setDrawColor(200, 200, 200);
        doc.line(14, headerY + 8, 196, headerY + 8);

        // Transaction Details
        doc.setFontSize(10);
        doc.setTextColor(40, 40, 40);
        
        const dateStr = new Date(txn.postedAt || txn.createdAt || txn.date).toLocaleString();
        const startDetailsY = headerY + 16;
        
        doc.text(`Receipt Reference: ${txn.id || txn.reference || 'N/A'}`, 14, startDetailsY);
        doc.text(`Date & Time: ${dateStr}`, 14, startDetailsY + 6);
        doc.text(`Folio ID: ${folio.folioId || folio.id || 'N/A'}`, 14, startDetailsY + 12);
        
        const isRefund = (txn.amount || txn.value) < 0 || txn.paymentMethod === 'Refund' || txn.type === 'Refund';
        const typeStr = isRefund ? 'Refund' : 'Payment';
        
        doc.text(`Transaction Type: ${typeStr}`, 14, startDetailsY + 18);

        const formattedAmount = formatCurrency(Math.abs(txn.amount || txn.value), currency);

        let txnDesc = txn.description || txn.category || `${typeStr} processed`;
        if (isUae) {
            txnDesc = txnDesc.replace(/gst\s*\([^)]*\)/gi, 'Statutory VAT').replace(/\b(cgst|sgst)\b/gi, 'VAT');
        }

        // Body Table
        doc.autoTable({
            startY: startDetailsY + 26,
            head: [['Description', 'Method / Ref', `Amount (${currency})`]],
            body: [
                [
                    txnDesc,
                    txn.reference || txn.method || txn.paymentMethod || 'N/A',
                    formattedAmount
                ]
            ],
            headStyles: { fillColor: isRefund ? [245, 158, 11] : [16, 185, 129] },
            theme: 'striped'
        });

        const finalY = doc.lastAutoTable.finalY || (startDetailsY + 50);
        
        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(40, 40, 40);
        doc.text(`Total ${typeStr}: ${formattedAmount}`, 196, finalY + 16, { align: "right" });
        
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(150, 150, 150);
        doc.text(`Thank you for choosing ${propertyName}.`, 105, finalY + 38, { align: "center" });
        doc.text("If you have any questions regarding this receipt, please contact the front desk.", 105, finalY + 44, { align: "center" });

        doc.save(`Receipt_${txn.id || txn.reference || 'Folio'}.pdf`);
    } catch (err) {
        console.error("Failed to generate PDF receipt:", err);
        alert("Failed to load PDF generation libraries.");
    }
};

/**
 * Generates an official Tax Invoice / Guest Folio PDF.
 * For UAE properties:
 *   - Displays TRN
 *   - Lists individual statutory line items:
 *     - VAT (5%)
 *     - Dubai Municipality Fee (7%)
 *     - Service Charge (10%)
 *     - Tourism Dirham (e.g. AED 15 / night)
 *   - Strictly removes any CGST / SGST lines
 * For non-UAE properties:
 *   - Retains GSTIN and existing CGST / SGST breakdown
 */
export const generateFolioInvoicePdf = async (folio, reservation = null, propertyInfo = null, activeTaxes = null) => {
    const loadScript = (src) => {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve(); return;
            }
            const script = document.createElement('script');
            script.src = src; script.onload = resolve; script.onerror = reject;
            document.head.appendChild(script);
        });
    };

    try {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.31/jspdf.plugin.autotable.min.js');
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF('portrait');

        const activeCur = getActiveCurrency();
        let adminAuth = {};
        try {
            adminAuth = JSON.parse(localStorage.getItem('adminAuth') || '{}');
        } catch (_) {}

        // Check active taxes for UAE/Dubai statutory indicators
        const hasUaeTaxes = Array.isArray(activeTaxes) && activeTaxes.some(t => {
            if (!t) return false;
            const name = String(t.name || '').toLowerCase();
            const st = String(t.serviceType || t.type || t.applicableTo || '').toLowerCase();
            return name.includes('dubai') || name.includes('uae') || name.includes('tourism dirham') || 
                   name.includes('municipality') || st.includes('tourism_dirham') || st.includes('municipality') ||
                   (t.rateType === 'flat_per_night');
        });

        // Check folio items for UAE indicators
        const hasUaeItems = (folio?.items || folio?.transactions || []).some(item => {
            const desc = String(item.description || item.category || '').toLowerCase();
            return desc.includes('dubai') || desc.includes('tourism dirham') || desc.includes('aed');
        });

        const isUae = (propertyInfo?.currency === 'AED') ||
                      (folio?.currency === 'AED') ||
                      (reservation?.currency === 'AED') ||
                      (activeCur === 'AED') ||
                      (localStorage.getItem('activeCurrency') === 'AED') ||
                      isUaeProperty(propertyInfo) ||
                      isUaeProperty(reservation?.hotel) ||
                      isUaeProperty(reservation?.property) ||
                      isUaeProperty(adminAuth?.tenant) ||
                      isUaeProperty(adminAuth?.hotel) ||
                      adminAuth?.currency === 'AED' ||
                      adminAuth?.hotel?.currency === 'AED' ||
                      adminAuth?.tenant?.currency === 'AED' ||
                      hasUaeTaxes ||
                      hasUaeItems;

        const rawCurrency = propertyInfo?.currency || folio?.currency || reservation?.currency || activeCur;
        const currency = isUae ? ((rawCurrency && rawCurrency !== 'INR') ? rawCurrency : 'AED') : (rawCurrency || 'INR');
        const propertyName = propertyInfo?.name || propertyInfo?.hotelName || reservation?.hotel?.name || adminAuth?.hotel?.name || adminAuth?.tenant?.name || "Willmount Resort";
        const propertyAddress = [propertyInfo?.address, propertyInfo?.city, propertyInfo?.country].filter(Boolean).join(', ') || 
                                [reservation?.hotel?.address, reservation?.hotel?.city, reservation?.hotel?.country].filter(Boolean).join(', ') ||
                                [adminAuth?.hotel?.address, adminAuth?.hotel?.city, adminAuth?.hotel?.country].filter(Boolean).join(', ');
        const taxLabel = isUae ? 'TRN' : 'GSTIN';
        const taxNumber = propertyInfo?.trn || propertyInfo?.trnNumber || propertyInfo?.gstin || propertyInfo?.taxId || adminAuth?.hotel?.trn || adminAuth?.tenant?.trn || reservation?.hotel?.trn || '';

        // Hotel Header
        doc.setFontSize(22);
        doc.setTextColor(33, 147, 176); 
        doc.text(propertyName, 105, 20, { align: "center" });
        
        let headerY = 26;
        if (propertyAddress) {
            doc.setFontSize(9);
            doc.setTextColor(120, 120, 120);
            doc.text(propertyAddress, 105, headerY, { align: "center" });
            headerY += 5;
        }

        if (taxNumber) {
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text(`${taxLabel}: ${taxNumber}`, 105, headerY, { align: "center" });
            headerY += 5;
        }

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(40, 40, 40);
        doc.text(isUae ? "TAX INVOICE" : "TAX INVOICE / GUEST FOLIO", 105, headerY + 4, { align: "center" });

        // Separator
        doc.setDrawColor(200, 200, 200);
        doc.line(14, headerY + 9, 196, headerY + 9);

        // Guest & Stay Info Grid
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(50, 50, 50);

        const guestName = reservation?.guestName || reservation?.guest?.name || [reservation?.guest?.firstName, reservation?.guest?.lastName].filter(Boolean).join(' ') || folio?.guestName || 'Valued Guest';
        const bookingRef = reservation?.bookingId || reservation?.id || folio?.reservationId || 'N/A';
        const folioId = folio?.id || folio?.folioId || 'N/A';
        const checkIn = reservation?.checkIn ? new Date(reservation.checkIn).toLocaleDateString() : '—';
        const checkOut = reservation?.checkOut ? new Date(reservation.checkOut).toLocaleDateString() : '—';
        const roomNumbers = (reservation?.rooms || []).map(r => r.roomNumber || r.room?.roomNumber).filter(Boolean).join(', ') || folio?.roomNumber || '—';

        const infoY = headerY + 16;
        doc.text(`Guest: ${guestName}`, 14, infoY);
        doc.text(`Booking Ref: ${bookingRef}`, 14, infoY + 5);
        doc.text(`Room(s): ${roomNumbers}`, 14, infoY + 10);

        doc.text(`Invoice Date: ${new Date().toLocaleDateString()}`, 130, infoY);
        doc.text(`Folio Ref: ${folioId}`, 130, infoY + 5);
        doc.text(`Stay Period: ${checkIn} - ${checkOut}`, 130, infoY + 10);

        // Process line items
        const rawItems = (folio?.items || folio?.transactions || []);
        const charges = rawItems.filter(item => !item.isVoided && (item.type === 'Charge' || item.category === 'Charge' || (!item.type && !item.category && Number(item.amount) > 0)));
        const payments = rawItems.filter(item => !item.isVoided && (item.type === 'Payment' || item.type === 'Deposit' || item.type === 'Refund' || item.category === 'Payment' || item.category === 'Deposit' || item.category === 'Refund' || Number(item.amount) < 0));

        const tableBody = [];
        let roomChargesTotal = 0;
        let otherChargesTotal = 0;

        charges.forEach((c) => {
            let desc = c.description || c.category || c.item || 'Room Accommodation';
            const amt = Math.abs(Number(c.amount || c.value || 0));
            const isRoom = desc.toLowerCase().includes('room') || desc.toLowerCase().includes('accommodation') || desc.toLowerCase().includes('tariff');
            
            if (isUae) {
                // Remove any CGST/SGST references for UAE properties
                desc = desc.replace(/gst\s*\([^)]*\)/gi, '').replace(/\b(cgst|sgst)\b/gi, '').trim();
            }

            if (isRoom) {
                roomChargesTotal += amt;
            } else {
                otherChargesTotal += amt;
            }

            tableBody.push([
                c.postedAt || c.createdAt || c.date ? new Date(c.postedAt || c.createdAt || c.date).toLocaleDateString() : '—',
                desc || 'Charge',
                c.reference || '—',
                formatCurrency(amt, currency)
            ]);
        });

        const totalFolioCharges = Number((roomChargesTotal + otherChargesTotal).toFixed(2));
        const grandTotal = totalFolioCharges > 0 ? totalFolioCharges : Number(folio?.totalAmount || 0);

        let subtotalBreakdownRows = [];
        const makeFootRow = (label, val) => [
            { content: label, colSpan: 3, styles: { halign: 'right' } },
            { content: val, styles: { halign: 'right' } }
        ];

        if (isUae) {
            const folioBreakdown = calculateDubaiFolioBreakdown({
                folio,
                reservation,
                tenant: propertyInfo,
                taxes: activeTaxes
            });

            const scPct = folioBreakdown.serviceChargePct !== undefined ? folioBreakdown.serviceChargePct : 10;
            const munPct = folioBreakdown.municipalityFeePct !== undefined ? folioBreakdown.municipalityFeePct : 7;
            const vatPct = folioBreakdown.vatPct !== undefined ? folioBreakdown.vatPct : 5;
            const tdRate = folioBreakdown.tourismDirhamRate || 15;

            // Only bill room-specific taxes if room charges exist on this folio
            const hasRoomCharges = roomChargesTotal > 0;
            const roomSc = hasRoomCharges ? (folioBreakdown.roomBreakdown?.serviceCharge || 0) : 0;
            const roomMun = hasRoomCharges ? (folioBreakdown.roomBreakdown?.municipalityFee || 0) : 0;
            const roomVat = hasRoomCharges ? (folioBreakdown.roomBreakdown?.vat || 0) : 0;
            const roomTd = hasRoomCharges ? (folioBreakdown.roomBreakdown?.tourismDirham || 0) : 0;

            const itemizedSc = folioBreakdown.itemizedBreakdown?.serviceCharge || 0;
            const itemizedMun = folioBreakdown.itemizedBreakdown?.municipalityFee || 0;
            const itemizedVat = folioBreakdown.itemizedBreakdown?.vat || 0;

            const serviceChargeAmt = Number((roomSc + itemizedSc).toFixed(2));
            const municipalityAmt = Number((roomMun + itemizedMun).toFixed(2));
            const vatAmt = Number((roomVat + itemizedVat).toFixed(2));
            const tourismDirhamAmt = Number(roomTd.toFixed(2));

            const statutoryTaxTotal = Number((serviceChargeAmt + municipalityAmt + vatAmt + tourismDirhamAmt).toFixed(2));
            const netTaxableBase = Math.max(0, Number((grandTotal - statutoryTaxTotal).toFixed(2)));

            if (hasRoomCharges && otherChargesTotal > 0) {
                const itemizedTaxes = Number((itemizedSc + itemizedMun + itemizedVat).toFixed(2));
                const itemizedBase = Math.max(0, Number((otherChargesTotal - itemizedTaxes).toFixed(2)));
                const roomBase = Math.max(0, Number((netTaxableBase - itemizedBase).toFixed(2)));
                subtotalBreakdownRows = [
                    makeFootRow('Net Room Base Tariff', formatCurrency(roomBase, currency)),
                    makeFootRow('Incidental Services (Net Base)', formatCurrency(itemizedBase, currency)),
                    ...(serviceChargeAmt > 0 ? [makeFootRow(`Service Charge (${scPct}%)`, formatCurrency(serviceChargeAmt, currency))] : []),
                    ...(municipalityAmt > 0 ? [makeFootRow(`Dubai Municipality Fee (${munPct}%)`, formatCurrency(municipalityAmt, currency))] : []),
                    ...(vatAmt > 0 ? [makeFootRow(`UAE VAT (${vatPct}%)`, formatCurrency(vatAmt, currency))] : []),
                    ...(tourismDirhamAmt > 0 ? [makeFootRow(`Tourism Dirham (AED ${tdRate} / night)`, formatCurrency(tourismDirhamAmt, currency))] : []),
                    makeFootRow('Total Invoice Value', formatCurrency(grandTotal, currency))
                ];
            } else if (hasRoomCharges) {
                subtotalBreakdownRows = [
                    makeFootRow('Net Room Base Tariff', formatCurrency(netTaxableBase, currency)),
                    ...(serviceChargeAmt > 0 ? [makeFootRow(`Service Charge (${scPct}%)`, formatCurrency(serviceChargeAmt, currency))] : []),
                    ...(municipalityAmt > 0 ? [makeFootRow(`Dubai Municipality Fee (${munPct}%)`, formatCurrency(municipalityAmt, currency))] : []),
                    ...(vatAmt > 0 ? [makeFootRow(`UAE VAT (${vatPct}%)`, formatCurrency(vatAmt, currency))] : []),
                    ...(tourismDirhamAmt > 0 ? [makeFootRow(`Tourism Dirham (AED ${tdRate} / night)`, formatCurrency(tourismDirhamAmt, currency))] : []),
                    makeFootRow('Total Invoice Value', formatCurrency(grandTotal, currency))
                ];
            } else {
                subtotalBreakdownRows = [
                    makeFootRow('Net Taxable Base', formatCurrency(netTaxableBase, currency)),
                    ...(serviceChargeAmt > 0 ? [makeFootRow(`Service Charge (${scPct}%)`, formatCurrency(serviceChargeAmt, currency))] : []),
                    ...(municipalityAmt > 0 ? [makeFootRow(`Dubai Municipality Fee (${munPct}%)`, formatCurrency(municipalityAmt, currency))] : []),
                    ...(vatAmt > 0 ? [makeFootRow(`UAE VAT (${vatPct}%)`, formatCurrency(vatAmt, currency))] : []),
                    makeFootRow('Total Invoice Value', formatCurrency(grandTotal, currency))
                ];
            }
        } else {
            // Standard Indian GST or International breakdown
            const gstPct = reservation?.gstPercentage || 12;
            const netBase = totalFolioCharges > 0 ? totalFolioCharges : Number(folio?.totalAmount || 0);
            const taxTotal = Number(((netBase * gstPct) / 100).toFixed(2));
            const cgstAmt = Number((taxTotal / 2).toFixed(2));
            const sgstAmt = Number((taxTotal / 2).toFixed(2));
            const invoiceVal = Number((netBase + taxTotal).toFixed(2));

            subtotalBreakdownRows = [
                makeFootRow('Taxable Base Value', formatCurrency(netBase, currency)),
                ...(otherChargesTotal > 0 && roomChargesTotal > 0 ? [makeFootRow('Other Charges', formatCurrency(otherChargesTotal, currency))] : []),
                makeFootRow(`CGST (${gstPct / 2}%)`, formatCurrency(cgstAmt, currency)),
                makeFootRow(`SGST (${gstPct / 2}%)`, formatCurrency(sgstAmt, currency)),
                makeFootRow('Total Invoice Value', formatCurrency(invoiceVal, currency))
            ];
        }

        const totalPaid = Math.abs(payments.reduce((s, p) => s + Math.abs(Number(p.amount || p.value || 0)), 0)) || Number(folio?.paidAmount || 0);
        const balanceDue = Math.max(0, Number((grandTotal - totalPaid).toFixed(2)));

        subtotalBreakdownRows.push(makeFootRow('Total Amount Paid', `- ${formatCurrency(totalPaid, currency)}`));
        subtotalBreakdownRows.push(makeFootRow('Net Balance Due', formatCurrency(balanceDue, currency)));

        doc.autoTable({
            startY: infoY + 18,
            head: [[
                { content: 'Date', styles: { halign: 'left' } },
                { content: 'Description / Service', styles: { halign: 'left' } },
                { content: 'Ref / Voucher', styles: { halign: 'center' } },
                { content: `Amount (${currency})`, styles: { halign: 'right' } }
            ]],
            body: tableBody.length > 0 ? tableBody : [['—', 'Room Accommodation & Services', '—', formatCurrency(grandTotal, currency)]],
            foot: subtotalBreakdownRows,
            showFoot: 'lastPage',
            rowPageBreak: 'avoid',
            theme: 'grid',
            headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] },
            footStyles: { fillColor: [248, 250, 252], textColor: [15, 23, 42], fontStyle: 'bold' },
            styles: { fontSize: 8.5, cellPadding: 2.5 },
            columnStyles: {
                0: { cellWidth: 26, halign: 'left' },
                1: { cellWidth: 'auto', halign: 'left' },
                2: { cellWidth: 24, halign: 'center' },
                3: { cellWidth: 42, halign: 'right' }
            }
        });

        let finalY = doc.lastAutoTable.finalY + 14;
        const pageHeight = doc.internal.pageSize.getHeight ? doc.internal.pageSize.getHeight() : 297;
        if (finalY + 10 > pageHeight - 15) {
            doc.addPage();
            finalY = 20;
        }

        doc.setFontSize(8.5);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);
        if (isUae) {
            doc.text("Statutory Dubai / UAE VAT & Hospitality Fee Regulation Notice: This document is an official Tax Invoice.", 14, finalY);
            doc.text("All statutory UAE VAT (5%), Dubai Municipality (7%), Service Charge (10%), and Tourism Dirham are detailed above.", 14, finalY + 5);
        } else {
            doc.text("Thank you for your patronage. Please settle any balance due upon presentation.", 14, finalY);
        }

        doc.save(`Invoice_${folioId}.pdf`);
    } catch (err) {
        console.error("Failed to generate folio invoice PDF:", err);
        alert("Failed to generate invoice PDF. Please ensure you have an active internet connection.");
    }
};

